import React, { useEffect, useState } from "react";
import client from "@/lib/api";
import { toast } from "sonner";
import { FileText, ImageOff, Loader2, ExternalLink, Paperclip, Trash2, Upload } from "lucide-react";

function EviThumb({ path, contentType, filename, onDelete }) {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);
  const isPdf = contentType === "application/pdf" || (filename || "").toLowerCase().endsWith(".pdf");
  useEffect(() => {
    if (isPdf) return;
    let url;
    client.get(`/rms/attachment/${path}`, { responseType: "blob" })
      .then((r) => { url = URL.createObjectURL(r.data); setSrc(url); })
      .catch(() => setFailed(true));
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [path, isPdf]);

  const open = async () => {
    try {
      const r = await client.get(`/rms/attachment/${path}`, { responseType: "blob" });
      const u = URL.createObjectURL(r.data);
      window.open(u, "_blank");
      setTimeout(() => URL.revokeObjectURL(u), 60000);
    } catch { toast.error("Could not open evidence file"); }
  };

  return (
    <div className="relative group">
      {isPdf ? (
        <button data-testid="rms-evidence-pdf" onClick={open}
          className="flex items-center gap-1.5 px-2 py-2 h-16 border border-[#0EA5E9]/50 bg-[#0d0e11] text-[#0EA5E9] text-[10px] font-mono hover:bg-[#0EA5E9]/10 transition-colors">
          <FileText size={14} /> <span className="max-w-[110px] truncate">{filename || "PDF"}</span> <ExternalLink size={10} />
        </button>
      ) : (
        <button data-testid="rms-evidence-image" onClick={open} title={filename}
          className="w-16 h-16 border border-[#2C2D35] bg-[#0d0e11] overflow-hidden flex items-center justify-center hover:border-[#0EA5E9] transition-colors">
          {failed ? <ImageOff size={16} className="text-[#4B5563]" />
            : src ? <img src={src} alt={filename} className="w-full h-full object-cover" />
            : <Loader2 size={14} className="animate-spin text-[#9CA3AF]" />}
        </button>
      )}
      {onDelete && (
        <button data-testid={`rms-evidence-delete-${filename}`} title="Remove evidence"
          onClick={() => onDelete(path, filename)}
          className="absolute -top-1.5 -right-1.5 bg-[#DC2626] text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
          <Trash2 size={12} />
        </button>
      )}
    </div>
  );
}

export default function RmsEvidence({ reportNumber, attachments = [], canManage, onChanged }) {
  const [busy, setBusy] = useState(false);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setBusy(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      await client.post(`/rms/${reportNumber}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Evidence attached");
      onChanged?.();
    } catch (e2) { toast.error(e2.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };

  const remove = async (path, filename) => {
    if (busy) return;
    if (!window.confirm(`Remove "${filename}" from this record?`)) return;
    setBusy(true);
    try {
      await client.delete(`/rms/${reportNumber}/attachments`, { params: { path } });
      toast.success("Evidence removed");
      onChanged?.();
    } catch (e2) { toast.error(e2.response?.data?.detail || "Could not remove"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid={`rms-evidence-${reportNumber}`} className="mt-1.5">
      <div className="font-mono text-[10px] text-[#9CA3AF] uppercase mb-1.5 flex items-center gap-1">
        <Paperclip size={11} /> Evidence / Records ({attachments.length})
        {canManage && (
          <label data-testid={`rms-evidence-upload-${reportNumber}`}
            className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 border border-[#0EA5E9]/50 text-[#0EA5E9] cursor-pointer hover:bg-[#0EA5E9]/10">
            {busy ? <Loader2 size={11} className="animate-spin" /> : <Upload size={11} />} Add
            <input type="file" multiple accept="image/*,.pdf" className="hidden" onChange={upload} disabled={busy} />
          </label>
        )}
      </div>
      {attachments.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {attachments.map((a, i) => (
            <EviThumb key={i} path={a.path} contentType={a.content_type} filename={a.filename}
              onDelete={canManage ? remove : undefined} />
          ))}
        </div>
      )}
    </div>
  );
}
